// Copyright (c) Jonathan Ferraz.
// Licensed under the MIT license.

import { Fragment, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { CardBlur } from 'components';
import FadeInSection from 'components/FadeInSection';

import { Container, Grid, Row, Wrapper } from './styles';
import { ReactComponent as BlueOneDesktop } from 'assets/icons/blue-one-desktop.svg';
import { ReactComponent as BlueRowFourDesktop } from 'assets/icons/blue-row-four.svg';
import { ReactComponent as InfinityX } from 'assets/icons/infinity-x.svg';
import BlueThree from 'assets/images/areMissing/layer-9-10_2x.webp';
import BlueTwo from 'assets/images/areMissing/layer-9-11_2x.webp';
import BlueFour from 'assets/images/areMissing/layer-9-8_2x.webp';
import BlueOne from 'assets/images/areMissing/layer-9-9_2x.webp';
import useScreenSize from 'hooks/useScreenSize';

const areMissingPrefix = 'pages.home.views.areMissing' as const;

export const AreMissing = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.home.views.areMissing',
  });

  const screenSize = useScreenSize();

  const isMobileQuery = useMemo(() => {
    if (screenSize === 'sm' || screenSize === 'xs') {
      return true;
    }

    return false;
  }, [screenSize]);

  return (
    <Container id={'are-missing'}>
      <Wrapper>
        <FadeInSection>
          {isMobileQuery ? (
            <h1>
              <Trans
                i18nKey={`${areMissingPrefix}.mobile.title-one`}
                components={{
                  break: <br />,
                }}
              />
            </h1>
          ) : (
            <h1>
              <Trans
                i18nKey={`${areMissingPrefix}.desktop.title-one`}
                components={{
                  break: <br />,
                }}
              />
            </h1>
          )}
        </FadeInSection>
        {isMobileQuery ? undefined : (
          <Fragment>
            <BlueOneDesktop />
            <div style={{ height: '350px' }} />
          </Fragment>
        )}

        <FadeInSection>
          {isMobileQuery ? (
            <h1>
              <Trans
                i18nKey={`${areMissingPrefix}.mobile.title-two`}
                components={{
                  break: <br />,
                }}
              />
            </h1>
          ) : (
            <h1>
              <Trans
                i18nKey={`${areMissingPrefix}.desktop.title-two`}
                components={{
                  break: <br />,
                }}
              />
            </h1>
          )}
        </FadeInSection>

        <Grid>
          <FadeInSection>
            <Row>
              <div className={'mobile-row'}>
                {isMobileQuery ? (
                  <img src={BlueOne} alt={'blue image'} />
                ) : undefined}
                <div>
                  <span>{t('span')}</span>
                  <h2>
                    <Trans
                      i18nKey={`${areMissingPrefix}.desktop.grids.one.hint`}
                    />
                  </h2>
                  <p>
                    {isMobileQuery ? (
                      <Trans
                        i18nKey={`${areMissingPrefix}.mobile.grids.one.text`}
                        components={{
                          break: <br />,
                          b: <strong />,
                        }}
                      />
                    ) : (
                      <Trans
                        i18nKey={`${areMissingPrefix}.desktop.grids.one.text`}
                        components={{
                          break: <br />,
                          b: <strong />,
                        }}
                      />
                    )}
                  </p>
                </div>
              </div>
            </Row>
          </FadeInSection>
          <FadeInSection>
            <Row>
              <div className={'mobile-row'}>
                <div>
                  <span>{t('span')}</span>
                  <h2>
                    <Trans
                      i18nKey={`${areMissingPrefix}.desktop.grids.two.hint`}
                    />
                  </h2>
                  <p>
                    {isMobileQuery ? (
                      <Trans
                        i18nKey={`${areMissingPrefix}.mobile.grids.two.text`}
                        components={{
                          break: <br />,
                          b: <strong />,
                        }}
                      />
                    ) : (
                      <Trans
                        i18nKey={`${areMissingPrefix}.desktop.grids.two.text`}
                        components={{
                          break: <br />,
                          b: <strong />,
                        }}
                      />
                    )}
                  </p>
                </div>
                {isMobileQuery ? (
                  <img src={BlueTwo} alt={'blue image'} />
                ) : undefined}
              </div>
            </Row>
          </FadeInSection>
          <FadeInSection>
            <Row>
              <div className={'mobile-row'}>
                {isMobileQuery ? (
                  <img src={BlueThree} alt={'blue image'} />
                ) : undefined}
                <div>
                  <span>{t('span')}</span>
                  <h2>
                    <Trans
                      i18nKey={`${areMissingPrefix}.desktop.grids.three.hint`}
                    />
                  </h2>
                  <p>
                    {isMobileQuery ? (
                      <Trans
                        i18nKey={`${areMissingPrefix}.mobile.grids.three.text`}
                        components={{
                          break: <br />,
                          b: <strong />,
                        }}
                      />
                    ) : (
                      <Trans
                        i18nKey={`${areMissingPrefix}.desktop.grids.three.text`}
                        components={{
                          break: <br />,
                          b: <strong />,
                        }}
                      />
                    )}
                  </p>
                </div>
              </div>
            </Row>
          </FadeInSection>
          <Row>
            {isMobileQuery ? undefined : <BlueRowFourDesktop />}

            <div className={'space'}>
              {isMobileQuery ? (
                <img src={BlueFour} alt={'blue image'} />
              ) : undefined}
              <CardBlur borderRadius={25}>
                <div className={'inside'}>
                  <div>
                    <InfinityX />
                    <p>
                      {isMobileQuery ? (
                        <Trans
                          i18nKey={`${areMissingPrefix}.mobile.grids.four.text`}
                          components={{
                            break: <br />,
                            b: <strong />,
                          }}
                        />
                      ) : (
                        <Trans
                          i18nKey={`${areMissingPrefix}.desktop.grids.four.text`}
                          components={{
                            break: <br />,
                            b: <strong />,
                          }}
                        />
                      )}
                    </p>
                  </div>
                </div>
              </CardBlur>
            </div>
          </Row>
        </Grid>
        {isMobileQuery ? undefined : <BlueOneDesktop />}
      </Wrapper>
    </Container>
  );
};
